














































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import gql from 'graphql-tag';
import Loading from '@/components/Loading.vue';
import LeaderboardRankings from '@/components/admin/LeaderboardRankings.vue';
import {
  ILeaderboard,
  PlayKind,
  pickCurrent,
  TEAM_SIZES_SHORT,
} from '@/utils/leaderboards.ts';
import {
  IQueryFailure,
  describeFailure,
  failureSummary,
  logQueryFailure,
} from '@/utils/queryLog.ts';

interface ICenter {
  id: string;
  centerCode: string;
  name: string;
}

// A selectable toggle option, backed by a single leaderboard.
interface IOption {
  key: string;
  label: string;
  board: ILeaderboard;
}

// The individual-performance-points play kinds, in display order.
const INDIVIDUAL_OPTIONS: Array<{
  key: string;
  label: string;
  playKind: PlayKind;
}> = [
  { key: 'all', label: 'All', playKind: 'ALL' },
  { key: 'open', label: 'Open Play', playKind: 'OPEN' },
  { key: 'league', label: 'League', playKind: 'LEAGUE' },
];

const ApolloOptions = Vue.extend({
  apollo: {
    centers: {
      query: gql`
        query Centers {
          centers {
            id
            centerCode
            name
          }
        }
      `,
      skip(): boolean {
        return !!(this as any).leaderboardId;
      },
      error(error: any) {
        (this as any).onListFailed('centers', error);
      },
    },
    // All active leaderboards across every scope (individual points and team
    // scores). The server applies no center filter for a null centerId, so we
    // scope client-side.
    leaderboards: {
      query: gql`
        query PublicLeaderboards {
          leaderboards(isActive: true) {
            id
            name
            centerId
            playKind
            rankingKind
            teamScoresTeamSize
            startTime
            endTime
            isActive
            createdAt
            updatedAt
          }
        }
      `,
      // Only needed in browse mode.
      skip(): boolean {
        return !!(this as any).leaderboardId;
      },
      error(error: any) {
        (this as any).onListFailed('leaderboards', error);
      },
    },
    // In specific (by-id) mode, look the board up so we can title the page with
    // its name. There is no single-leaderboard query and the board may be
    // inactive, so we fetch unfiltered and find it client-side.
    specificBoards: {
      query: gql`
        query LeaderboardLookup {
          leaderboards {
            id
            name
            rankingKind
          }
        }
      `,
      update: (data: any) => data.leaderboards,
      skip(): boolean {
        return !(this as any).leaderboardId;
      },
      error(error: any) {
        (this as any).onListFailed('leaderboards', error);
      },
    },
  },
});

@Component({
  components: { Loading, LeaderboardRankings },
})
export default class LeaderboardView extends ApolloOptions {

  get leaderboardId(): string | null {
    return (this.$route.params.id as string) || null;
  }

  get specificBoard(): {
    id: string;
    name: string;
    rankingKind?: string;
  } | null {
    return (
      (this.specificBoards || []).find((b) => b.id === this.leaderboardId) || null
    );
  }

  get title(): string {
    if (!this.leaderboardId) {
      return 'Leader Board';
    }
    return this.specificBoard ? this.specificBoard.name : 'Leaderboard';
  }

  get loaded(): boolean {
    return this.centers !== null && this.leaderboards !== null;
  }

  get centerCode(): string {
    return (this.$route.query.centerCode as string) || '';
  }

  get selectedCenterId(): string | null {
    if (!this.centerCode || !this.centers) {
      return null;
    }
    const match = this.centers.find((c) => c.centerCode === this.centerCode);
    return match ? match.id : null;
  }

  get centerName(): string {
    if (!this.centerCode || !this.centers) {
      return 'National';
    }
    const match = this.centers.find((c) => c.centerCode === this.centerCode);
    return match ? match.name : 'National';
  }

  // Active boards for the selected scope (global or center).
  get scopedBoards(): ILeaderboard[] {
    return (this.leaderboards || []).filter(
      (lb) =>
        this.selectedCenterId
          ? lb.centerId === this.selectedCenterId
          : lb.centerId == null,
    );
  }

  // The toggle options available for this scope: the individual-points play
  // kinds that have a board, plus one "team score" option per team size that
  // has an active team-scores board.
  get options(): IOption[] {
    const scoped = this.scopedBoards;
    const opts: IOption[] = [];

    for (const o of INDIVIDUAL_OPTIONS) {
      const board = pickCurrent(
        scoped.filter(
          (b) =>
            b.rankingKind === 'INDIVIDUAL_PERFORMANCE_POINTS' &&
            b.playKind === o.playKind,
        ),
      );
      if (board) {
        opts.push({ key: o.key, label: o.label, board });
      }
    }

    const teamBoards = scoped.filter((b) => b.rankingKind === 'TEAM_SCORES');
    for (const size of TEAM_SIZES_SHORT) {
      const board = pickCurrent(
        teamBoards.filter(
          (b) => (b.teamScoresTeamSize || null) === size.size,
        ),
      );
      if (board) {
        opts.push({ key: `team-${size.size}`, label: size.label, board });
      }
    }

    return opts;
  }

  // The individual-points sub-options (dropdown choices).
  get individualOptions(): IOption[] {
    return this.options.filter((o) => !this.isTeamKey(o.key));
  }

  // The team-scores sub-options (one per available team size), dropdown choices
  // shown when the "League Team Total Score" toggle is active.
  get teamOptions(): IOption[] {
    return this.options.filter((o) => this.isTeamKey(o.key));
  }

  // The sub-options for the currently active top-level toggle.
  get dropdownOptions(): IOption[] {
    return this.selectedMode === 'team'
      ? this.teamOptions
      : this.individualOptions;
  }

  get selectedOption(): IOption | null {
    const key = ((this.$route.query.playKind as string) || '').toLowerCase();
    return this.options.find((o) => o.key === key) || this.options[0] || null;
  }

  // Which top-level toggle is active: individual points vs league team score.
  get selectedMode(): 'individual' | 'team' {
    return this.selectedOption && this.isTeamKey(this.selectedOption.key)
      ? 'team'
      : 'individual';
  }

  get selectedBoard(): ILeaderboard | null {
    return this.selectedOption ? this.selectedOption.board : null;
  }

  // The score column reads "Score" for team totals, "Points" otherwise.
  get scoreLabel(): string {
    const board = this.leaderboardId ? this.specificBoard : this.selectedBoard;
    return board && board.rankingKind === 'TEAM_SCORES' ? 'Score' : 'Points';
  }
  public centers: ICenter[] | null = null;
  public leaderboards: ILeaderboard[] | null = null;
  public specificBoards: Array<{
    id: string;
    name: string;
    rankingKind?: string;
  }> | null = null;

  // Set when the list of centers or leaderboards could not be loaded; without it the page would
  // show the loading indicator forever.
  public loadFailure: IQueryFailure | null = null;
  private startedAt = Date.now();

  get loadFailureDetail(): string {
    return this.loadFailure ? failureSummary(this.loadFailure) : '';
  }

  public onListFailed(query: string, error: any) {
    this.loadFailure = describeFailure(error, this.startedAt);
    logQueryFailure(query, { centerCode: this.centerCode }, this.loadFailure);
  }

  // Records which board the page chose and what it chose from: several boards can match one
  // toggle option, and only the most recent active one is shown.
  @Watch('loaded')
  @Watch('selectedBoard')
  public onBoardSelected() {
    if (!this.loaded || this.leaderboardId) {
      return;
    }
    const board = this.selectedBoard;
    // tslint:disable-next-line:no-console
    console.info('[leaderboard] showing', board ? board.id : 'no board', {
      center: this.centerCode || 'National',
      option: this.selectedOption ? this.selectedOption.key : null,
      board,
      boardsInScope: this.scopedBoards.length,
      options: this.options.map((o) => `${o.key}=${o.board.id}`),
    });
  }

  public onSelectCenter(centerCode: string) {
    // Drop playKind so it re-defaults to an available option for the new center.
    const query: { [k: string]: string } = {};
    if (centerCode) {
      query.centerCode = centerCode;
    }
    this.$router.replace({ query });
  }

  public onSelectOption(key: string) {
    const query = { ...this.$route.query, playKind: key };
    this.$router.replace({ query });
  }

  // Switch the top-level toggle. Each mode defaults to its first available
  // sub-option (first team size, or first individual play kind).
  public onSelectMode(mode: 'individual' | 'team') {
    if (this.selectedMode === mode) {
      return;
    }
    const target =
      mode === 'team' ? this.teamOptions : this.individualOptions;
    if (target.length) {
      this.onSelectOption(target[0].key);
    }
  }

  private isTeamKey(key: string): boolean {
    return key.indexOf('team-') === 0;
  }
}
